import React from 'react';
import { EnvelopeIcon, ClockIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

/**
 * Contact page component
 * Provides company contact information
 * 
 * @returns {JSX.Element} The rendered Contact page
 */
const ContactPage: React.FC = () => {
  try {
    // Log component rendering
    console.log('Rendering Contact page');

    // Office location
    const officeLocation = {
      address: '271 Little Falls Dr.',
      cityState: 'Wilmington, Delaware 19808',
      country: 'United States',
    };
    
    // Contact information
    const contactInfo = {
      email: 'contact@raidu.com',
      supportHours: 'Monday - Friday: 9AM - 6PM EST',
      sales: 'sales@raidu.com',
      support: 'support@raidu.com',
      press: 'press@raidu.com',
    };

    return (
      <div className="relative overflow-hidden">
        {/* Decorative elements matching Hero section */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-indigo-200 blur-3xl"></div>
          <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-purple-200 blur-3xl"></div>
          <div className="absolute -bottom-24 left-1/4 w-72 h-72 rounded-full bg-pink-200 blur-3xl"></div>
        </div>
        
        {/* Subtle dot pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10" 
          style={{
            backgroundImage: 'radial-gradient(#4f46e5 0.5px, transparent 0.5px)',
            backgroundSize: '12px 12px'
          }}
        ></div>
        
        {/* Hero section */}
        <div className="relative">
          <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Get in Touch With Raidu
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600 max-w-3xl">
              Have questions about Raidu AI governance solutions? Our team is here to help. 
              Get in touch with us for sales inquiries, support, or general questions.
            </p>
          </div>
        </div>

        {/* Contact information */}
        <div className="mx-auto max-w-7xl px-6 py-10 sm:py-14 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-5 col-span-1 md:col-span-2 lg:col-span-3">Contact Information</h2>
            
            {/* Address */}
            <div className="bg-white shadow-lg rounded-xl p-4 border border-indigo-100 h-full">
              <div className="flex items-center mb-4">
                <BuildingOfficeIcon className="h-6 w-6 text-indigo-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">Our Address</h3>
              </div>
              <address className="not-italic text-gray-600 ml-8">
                <p>{officeLocation.address}</p>
                <p>{officeLocation.cityState}</p>
                <p>{officeLocation.country}</p>
              </address>
            </div>
            
            {/* Email */}
            <div className="bg-white shadow-lg rounded-xl p-4 border border-indigo-100 h-full">
              <div className="flex items-center mb-4">
                <EnvelopeIcon className="h-6 w-6 text-indigo-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">Email</h3>
              </div>
              <div className="ml-8 space-y-1">
                <p className="text-gray-600">{contactInfo.email}</p>
              </div>
            </div>
            
            {/* Support Hours */}
            <div className="bg-white shadow-lg rounded-xl p-4 border border-indigo-100 h-full">
              <div className="flex items-center mb-4">
                <ClockIcon className="h-6 w-6 text-indigo-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">Support Hours</h3>
              </div>
              <div className="ml-8">
                <p className="text-gray-600">{contactInfo.supportHours}</p>
              </div>
            </div>
            
            {/* Department contacts section header */}
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4 col-span-1 md:col-span-2 lg:col-span-3">Department Contacts</h3>
            
            {/* Sales */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl border border-indigo-100 h-full">
              <h4 className="text-base font-semibold text-gray-900">Sales</h4>
              <p className="mt-2 text-gray-600">Talk to our sales team about Raidu solutions</p>
              <div className="mt-3 flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-indigo-500" />
                <span className="ml-2 text-gray-700">{contactInfo.sales}</span>
              </div>
            </div>
            
            {/* Support */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl border border-indigo-100 h-full">
              <h4 className="text-base font-semibold text-gray-900">Support</h4>
              <p className="mt-2 text-gray-600">Get help with your Raidu implementation</p>
              <div className="mt-3 flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-indigo-500" />
                <span className="ml-2 text-gray-700">{contactInfo.support}</span>
              </div>
            </div>
            
            {/* Press */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl border border-indigo-100 h-full">
              <h4 className="text-base font-semibold text-gray-900">Press</h4>
              <p className="mt-2 text-gray-600">Media inquiries and press kit requests</p>
              <div className="mt-3 flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-indigo-500" />
                <span className="ml-2 text-gray-700">{contactInfo.press}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    // Log any errors during rendering
    console.error('Error rendering Contact page:', error);
    
    // Return error state
    return (
      <div className="py-12 px-6 bg-red-50 rounded-lg mx-auto max-w-7xl">
        <h2 className="text-2xl font-bold text-red-700 mb-4">Error Loading Contact Page</h2>
        <p className="text-red-600">
          We encountered an issue while loading this page. Please try refreshing or contact support.
        </p>
      </div>
    );
  }
};

export default ContactPage;
